import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  Tabs,
  Group,
  Button,
  Badge,
  Loader,
  Table,
  Text,
  MultiSelect,
  Stack,
  Paper,
  RingProgress,
  Tooltip,
  Collapse,
  ActionIcon,
  Center,
  Grid,
} from '@mantine/core';
import { useDevice } from '../../../hooks/useDevice';
import { RoundDetailHero } from './parts/RoundDetailHero';
import { StageHistoryCard } from './parts/StageHistoryCard';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconArrowRight, IconBell, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { showToast } from '../../../utils/toast';
import { api } from '../../../utils/api';
import { perfReviewErrorMessage } from '../utils/errorMessage';
import { PageContainer } from '../../../components/ui/PageContainer';
import { LoadFailed } from '../../../components/ui/LoadFailed';
import { BackButton } from '../../../components/ui/BackButton';
import {
  useGetPerformanceReviewRoundsId,
  useGetPerformanceReviewRoundsIdProgress,
  useGetPerformanceReviewRoundsRoundIdParticipants,
  useGetPerformanceReviewRoundsRoundIdForms,
  useGetPerformanceReviewRoundsRoundIdAssignments,
  usePostPerformanceReviewRoundsIdTransitionsTargetStage,
  usePostPerformanceReviewRoundsRoundIdParticipants,
  useDeletePerformanceReviewRoundsRoundIdParticipantsUserId,
  useDeletePerformanceReviewRoundsId,
  getGetPerformanceReviewRoundsQueryKey,
  useGetPerformanceReviewRounds,
  useGetPerformanceReviewRoundsRoundIdSummarizers,
} from '../../../api/generated/performance-review/performance-review';
import { SummarizersPanel } from './parts/SummarizersPanel';
import { useNavigate } from 'react-router-dom';
import { useUsersDirectory } from '../../../hooks/useUserApi';
import { useTeamsOverview } from '../../../hooks/useTeamApi';
import { usePermission } from '../../../hooks/usePermission';
import { PerformanceReviewAssignmentsEditor } from '../../../components/review/PerformanceReviewAssignmentsEditor';
import type { OutcomeValues } from '../../../components/review/PerformanceReviewOutcomePanel';
import { PerformanceReviewSummaryEditModal } from '../../../components/review/PerformanceReviewSummaryEditModal';
import { PerformanceReviewRound } from '../../../api/generated/model/performanceReviewRound';
import { useUserStore } from '../../../store/userStore';

type TransitionStage = 'collect-essay' | 'collect-feedback' | 'summary' | 'finished' | 'archived';

const NEXT_STAGE: Record<string, TransitionStage> = {
  draft: 'collect-essay',
  'collect-essay': 'collect-feedback',
  'collect-feedback': 'summary',
  summary: 'finished',
  finished: 'archived',
};

const STAGE_KEY: Record<string, string> = {
  draft: 'draft',
  'collect-essay': 'collectEssay',
  'collect-feedback': 'collectFeedback',
  summary: 'summary',
  finished: 'finished',
  archived: 'archived',
};

interface PopulatedParticipant extends OutcomeValues {
  _id?: string;
  user?: { _id?: string; name?: string } | string;
  requestedReviewers?: string[];
  summaryDraft?: string;
  summaryPublished?: string;
  summaryPublishedAt?: string;
  summarySharedAt?: string;
  summaryBlocks?: { block: string; comment: string }[];
}

interface FormDoc {
  _id?: string;
  type?: 'self' | 'peer' | 'manager';
  status?: 'pending' | 'draft' | 'submitted';
  lastReminderAt?: string;
  respondent?: string | { _id?: string };
  subject?: string | { _id?: string };
}

interface AssignmentDoc {
  subject?: string;
  respondent?: string;
}

// The backend stores a reviewer's form as `manager` (not `peer`) whenever the
// respondent leads or PMs the subject's team. Both count as feedback everywhere
// — progress rings on the backend already aggregate over both types.
const isFeedbackForm = (f: FormDoc) => f.type === 'peer' || f.type === 'manager';

const idOf = (v?: string | { _id?: string }) => (typeof v === 'object' ? v?._id : v);

interface RoundProgress {
  participants?: number;
  reminders?: number;
  selfForms?: { submitted?: number; total?: number };
  peerForms?: { submitted?: number; total?: number };
  summaries?: { published?: number; total?: number };
}

const TAB_VALUES = ['participants', 'assignments', 'summarizers', 'progress', 'summary'] as const;
type TabValue = (typeof TAB_VALUES)[number];

export const PerformanceReviewAdminRound: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  // Страницу открывает reviews.manage (гард маршрута). Прогресс — reviews.progress, саммари за
  // недоступного автора — тот, кто видит сырой фидбек: «Настройщику» этих вкладок нет.
  const canSeeProgress = usePermission('reviews.progress');
  const canWriteSummaries = usePermission('reviews.feedback.raw');
  const isTabAllowed = (tab: string) =>
    (tab !== 'progress' || canSeeProgress) && (tab !== 'summary' || canWriteSummaries);
  const tabParam = searchParams.get('tab');
  const activeTab: TabValue =
    (TAB_VALUES as readonly string[]).includes(tabParam ?? '') && isTabAllowed(tabParam ?? '')
      ? (tabParam as TabValue)
      : 'participants';
  const handleTabChange = (value: string | null) => {
    if (!value) return;
    // push (not replace) so browser back/forward steps through visited tabs;
    // preserve any other query params already on the URL.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', value);
      return next;
    });
  };
  const user = useUserStore((s) => s.user);
  const { t, i18n } = useTranslation();
  const device = useDevice();
  const isMobile = device === 'mobile';
  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'ru-RU';
  const stageLabel = (s: string) => t(`perfReview.stage.${STAGE_KEY[s]}`);
  const liveQuery = { refetchInterval: 10_000, staleTime: 0 };
  const {
    data: round,
    refetch: refetchRound,
    isError: isRoundError,
  } = useGetPerformanceReviewRoundsId(id!, {
    query: liveQuery,
  });
  const { data: progress, refetch: refetchProgress } = useGetPerformanceReviewRoundsIdProgress(
    id!,
    { query: { ...liveQuery, enabled: canSeeProgress } },
  );
  const { data: participants = [], refetch: refetchParticipants } =
    useGetPerformanceReviewRoundsRoundIdParticipants(id!, { query: liveQuery });
  const { data: formsRaw = [], refetch: refetchForms } = useGetPerformanceReviewRoundsRoundIdForms(
    id!,
    { query: liveQuery },
  );
  const { data: assignmentsRaw = [] } = useGetPerformanceReviewRoundsRoundIdAssignments(id!, {
    query: liveQuery,
  });
  const { data: summarizers } = useGetPerformanceReviewRoundsRoundIdSummarizers(id!);
  // См. SummarizersPanel: клиент отдаёт тело ответа напрямую, без обёртки.
  const summarizerRows =
    (summarizers as unknown as { summarizer?: { _id?: string } }[] | undefined) || [];
  const unassignedSummarizers = summarizerRows.filter((row) => !row.summarizer).length;
  const { data: allRounds = [] } = useGetPerformanceReviewRounds();
  // Источники для «скопировать назначения» — все прочие раунды, свежие сверху.
  const previousRounds = useMemo(
    () =>
      ((allRounds as unknown as { _id: string; title: string }[]) || [])
        .filter((round) => round._id !== id)
        .map((round) => ({ _id: round._id, title: round.title })),
    [allRounds, id],
  );
  const transition = usePostPerformanceReviewRoundsIdTransitionsTargetStage();
  const addParticipant = usePostPerformanceReviewRoundsRoundIdParticipants();
  const removeParticipant = useDeletePerformanceReviewRoundsRoundIdParticipantsUserId();
  const deleteRound = useDeletePerformanceReviewRoundsId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Справочники людей и команд, открытые всем: /users и /teams — только админу.
  const { data: users = [] } = useUsersDirectory();
  // Кандидаты в раунд — только кто пользуется Performance Review (раздел мог быть сужен до ролей).
  const { data: candidates } = useUsersDirectory('performanceReview');
  const { data: teams = [] } = useTeamsOverview();
  const [summaryUser, setSummaryUser] = useState<PopulatedParticipant | null>(null);
  const [pickedUsers, setPickedUsers] = useState<string[]>([]);
  const [isAddOpen, addControls] = useDisclosure(true);

  const ps = useMemo(
    () => (participants as unknown as PopulatedParticipant[]) || [],
    [participants],
  );
  // An admin running the round shouldn't read (or edit) the summary written
  // about themselves, so the Summary tab drops the current admin's own row
  // (`visibleParticipants`). The Participants tab keeps the full `ps` — it's
  // pure membership management (add/remove in draft), where hiding your own row
  // would strand you in the round with no way out — and Progress stays complete
  // so round-completion tracking still reconciles with its rings.
  const myId = user?._id;
  const visibleParticipants = useMemo(
    () => ps.filter((p) => idOf(p.user as string | { _id?: string }) !== myId),
    [ps, myId],
  );
  const forms = useMemo(() => (formsRaw as unknown as FormDoc[]) || [], [formsRaw]);
  const assignments = useMemo(
    () => (assignmentsRaw as unknown as AssignmentDoc[]) || [],
    [assignmentsRaw],
  );
  const allUsers = useMemo(
    () => (users as unknown as { _id: string; name: string }[]) || [],
    [users],
  );
  const teamsTyped = useMemo(
    () =>
      (teams as unknown as {
        _id: string;
        name: string;
        members?: { user?: { _id: string } }[];
        lead?: { _id: string };
      }[]) || [],
    [teams],
  );
  const r = round as unknown as PerformanceReviewRound | undefined;
  const prog = progress as unknown as RoundProgress | undefined;

  const userNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of allUsers) map[u._id] = u.name;
    return map;
  }, [allUsers]);

  // Кто не сдал формы текущей стадии — им можно напомнить. Ключ — респондент,
  // значение — когда ему напоминали последний раз (0 — ни разу).
  const isCollecting =
    r?.status === 'active' && (r.stage === 'collect-essay' || r.stage === 'collect-feedback');
  const owingByUser = useMemo(() => {
    const map = new Map<string, number>();
    if (!isCollecting) return map;
    for (const f of forms) {
      const isStageForm = r?.stage === 'collect-essay' ? f.type === 'self' : isFeedbackForm(f);
      const rid = idOf(f.respondent);
      if (!isStageForm || f.status === 'submitted' || !rid) continue;
      const remindedAt = f.lastReminderAt ? new Date(f.lastReminderAt).getTime() : 0;
      map.set(rid, Math.max(map.get(rid) ?? 0, remindedAt));
    }
    return map;
  }, [forms, isCollecting, r?.stage]);
  const [remindingKey, setRemindingKey] = useState<string | null>(null);

  // userId — одному, без него — всем, кто не сдал. Бэкенд не шлёт повторно
  // тем, кому напоминали в последний час, и возвращает, скольким ушло письмо.
  const sendReminders = async (userId?: string) => {
    setRemindingKey(userId ?? 'all');
    try {
      const { reminded } = (await api(`/performance-review/rounds/${id}/remind`, {
        method: 'POST',
        body: userId ? { userId } : {},
      })) as { reminded: number };
      showToast({
        title: reminded
          ? t('perfReview.remind.done', { count: reminded })
          : t('perfReview.remind.skipped'),
        message: '',
        color: reminded ? 'teal' : 'yellow',
      });
      void refetchForms();
      void refetchProgress();
    } catch (e) {
      showToast({ title: t('common.error'), message: perfReviewErrorMessage(t, e), color: 'red' });
    } finally {
      setRemindingKey(null);
    }
  };

  const confirmRemindAll = () =>
    modals.openConfirmModal({
      title: t('perfReview.remind.confirmTitle'),
      children: t('perfReview.remind.confirmBody', { count: owingByUser.size }),
      labels: { confirm: t('perfReview.remind.confirm'), cancel: t('common.cancel') },
      onConfirm: () => sendReminders(),
    });

  // Статус self-эссе по сотруднику. Нет записи — self-эссе у него нет вовсе:
  // ревьюер, втянутый в раунд без участия, видит «нет self», а не «—».
  const selfStatusByUser = useMemo(() => {
    const map = new Map<string, FormDoc['status']>();
    for (const f of forms) {
      const sid = idOf(f.subject);
      if (f.type === 'self' && sid) map.set(sid, f.status);
    }
    return map;
  }, [forms]);

  const peerStatsBySubject = useMemo(() => {
    const total: Record<string, number> = {};
    const submitted: Record<string, number> = {};
    for (const f of forms) {
      if (!isFeedbackForm(f)) continue;
      const sid = idOf(f.subject);
      if (!sid) continue;
      total[sid] = (total[sid] || 0) + 1;
      if (f.status === 'submitted') submitted[sid] = (submitted[sid] || 0) + 1;
    }
    return { total, submitted };
  }, [forms]);

  // Mirror stats from the OTHER direction — how many peer-forms each user
  // (as respondent) is owing / has submitted. Used to show two columns:
  // "ОТРЕВЬЮИЛ" (this user as respondent) vs "ЕГО ОТРЕВЬЮИЛИ" (as subject).
  const peerStatsByRespondent = useMemo(() => {
    const total: Record<string, number> = {};
    const submitted: Record<string, number> = {};
    for (const f of forms) {
      if (!isFeedbackForm(f)) continue;
      const rid = idOf(f.respondent);
      if (!rid) continue;
      total[rid] = (total[rid] || 0) + 1;
      if (f.status === 'submitted') submitted[rid] = (submitted[rid] || 0) + 1;
    }
    return { total, submitted };
  }, [forms]);

  const assignedCountBySubject = useMemo(() => {
    const map: Record<string, number> = {};
    for (const a of assignments) {
      if (a.subject) map[a.subject] = (map[a.subject] || 0) + 1;
    }
    return map;
  }, [assignments]);

  // Progress covers everyone touched by the round, not just its participants:
  // a user can review others (or be reviewed) without ever taking part as a
  // subject, and those rows were silently dropped before.
  const progressRows = useMemo(() => {
    const rows = new Map<
      string,
      { userId: string; name: string; participant?: PopulatedParticipant }
    >();
    for (const p of ps) {
      const uid = idOf(p.user as string | { _id?: string });
      if (!uid) continue;
      const name = (typeof p.user === 'object' ? p.user?.name : undefined) || userNameById[uid];
      rows.set(uid, { userId: uid, name: name || uid, participant: p });
    }
    const addOutsider = (uid?: string) => {
      if (!uid || rows.has(uid)) return;
      rows.set(uid, { userId: uid, name: userNameById[uid] || uid });
    };
    for (const f of forms) {
      if (!isFeedbackForm(f)) continue;
      addOutsider(idOf(f.respondent));
      addOutsider(idOf(f.subject));
    }
    for (const a of assignments) {
      addOutsider(a.respondent);
      addOutsider(a.subject);
    }
    return Array.from(rows.values()).sort((a, b) => {
      const aOutside = a.participant ? 0 : 1;
      const bOutside = b.participant ? 0 : 1;
      if (aOutside !== bOutside) return aOutside - bOutside;
      return a.name.localeCompare(b.name);
    });
  }, [ps, forms, assignments, userNameById]);

  if (isRoundError && !r)
    return (
      <PageContainer>
        <LoadFailed onBack={() => navigate('/performance-review/admin')} />
      </PageContainer>
    );
  if (!r)
    return (
      <PageContainer>
        <Center mih="60vh">
          <Loader size="sm" />
        </Center>
      </PageContainer>
    );

  const doTransition = async (target: TransitionStage) => {
    try {
      await transition.mutateAsync({ id: id!, targetStage: target });
      await refetchRound();
      showToast({ title: t('perfReview.stageMoved'), message: stageLabel(target), color: 'teal' });
    } catch (e) {
      showToast({
        title: t('perfReview.stageMoveFailed'),
        message: perfReviewErrorMessage(t, e),
        color: 'red',
      });
    }
  };

  const handleDelete = () => {
    modals.openConfirmModal({
      title: t('perfReview.deleteRound.confirmTitle'),
      children: t('perfReview.deleteRound.confirmBody', {
        title: r.title || '',
      }),
      labels: {
        confirm: t('perfReview.deleteRound.confirmAccept'),
        cancel: t('perfReview.deleteRound.confirmCancel'),
      },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteRound.mutateAsync({ id: id! });
          const roundId = id!;
          // Drop the deleted round's own cached queries so nothing refetches it
          // (avoids the 404s on round/participants/forms/assignments), then
          // refresh the rounds list + stats so it disappears without a reload.
          queryClient.removeQueries({
            predicate: (q) => q.queryKey.some((k) => typeof k === 'string' && k.includes(roundId)),
          });
          queryClient.invalidateQueries({ queryKey: getGetPerformanceReviewRoundsQueryKey() });
          showToast({
            title: t('perfReview.deleteRound.success'),
            message: r.title || '',
            color: 'green',
          });
          navigate('/performance-review/admin');
        } catch (e) {
          showToast({
            title: t('perfReview.deleteRound.errorGeneric'),
            message: perfReviewErrorMessage(t, e),
            color: 'red',
          });
        }
      },
    });
  };

  const handleNext = () => {
    const target = r.stage ? NEXT_STAGE[r.stage] : undefined;
    if (!target) return;

    // Бэкенд не пустит раунд в стадию саммари, пока у кого-то нет
    // ответственного. Ловим это здесь, чтобы вместо 409 показать, куда идти.
    if (target === 'summary' && unassignedSummarizers > 0) {
      showToast({
        title: t('perfReview.summarizerMissingTitle'),
        message: t('perfReview.summarizerMissing', { unassigned: unassignedSummarizers }),
        color: 'red',
      });
      handleTabChange('summarizers');
      return;
    }

    // Считаем готовность на основе текущего forms-снимка
    let warningBody: React.ReactNode = null;
    if (r.stage === 'collect-essay') {
      const totalSelf = forms.filter((fd) => fd.type === 'self').length;
      const submittedSelf = forms.filter(
        (fd) => fd.type === 'self' && fd.status === 'submitted',
      ).length;
      if (totalSelf > 0 && submittedSelf < totalSelf) {
        warningBody = t('perfReview.confirmSelfIncomplete', {
          submitted: submittedSelf,
          total: totalSelf,
        });
      }
    } else if (r.stage === 'collect-feedback') {
      const totalPeer = forms.filter(isFeedbackForm).length;
      const submittedPeer = forms.filter(
        (fd) => isFeedbackForm(fd) && fd.status === 'submitted',
      ).length;
      if (totalPeer > 0 && submittedPeer < totalPeer) {
        warningBody = t('perfReview.confirmPeerIncomplete', {
          submitted: submittedPeer,
          total: totalPeer,
        });
      }
    }

    if (warningBody) {
      modals.openConfirmModal({
        title: t('perfReview.confirmMoveTitle', { name: stageLabel(target) }),
        centered: true,
        children: <Text size="sm">{warningBody}</Text>,
        labels: { confirm: t('perfReview.confirmMove'), cancel: t('perfReview.confirmCancel') },
        onConfirm: () => {
          doTransition(target);
        },
      });
      return;
    }

    doTransition(target);
  };

  const isArchived = r.status === 'archived';
  const nextStageKey = r.stage && !isArchived ? NEXT_STAGE[r.stage] : undefined;

  const inRoundIds: string[] = [];
  ps.forEach((p) => {
    const uid = typeof p.user === 'object' ? p.user?._id : p.user;
    if (uid) inRoundIds.push(uid);
  });
  const isInRound = (uid: string) => inRoundIds.indexOf(uid) !== -1;
  const candidateIds = candidates ? new Set(candidates.map((u) => u._id)) : null;
  const isCandidate = (uid: string) => !candidateIds || candidateIds.has(uid);
  const availableOptions = allUsers
    .filter((u) => !isInRound(u._id) && isCandidate(u._id))
    .map((u) => ({ value: u._id, label: u.name }));
  const mergeIds = (extra: string[]) => {
    setPickedUsers((prev) => {
      const result = prev.slice();
      extra.forEach((uid) => {
        if (!isInRound(uid) && isCandidate(uid) && result.indexOf(uid) === -1) result.push(uid);
      });
      return result;
    });
  };

  const stageHistory =
    (r.stageHistory as unknown as Array<{
      stage?: import('../../../api/generated/model/performanceReviewRoundStage').PerformanceReviewRoundStage;
      transitionedAt?: string | Date;
      transitionedBy?: string | { name?: string };
      mode?: 'manual' | 'auto-completion';
    }>) || [];

  return (
    <PageContainer>
      <Stack gap="lg">
        <Group justify="space-between">
          <BackButton onClick={() => navigate('/performance-review/admin')} />
          {/* Итоги есть с этапа саммари; до него страница результатов пуста. */}
          {(r.stage === 'summary' || r.stage === 'finished') && (
            <Button
              variant="light"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate(`/performance-review/${id}/results`)}
            >
              {t('perfReview.adminHome.round.openResults')}
            </Button>
          )}
        </Group>
        <RoundDetailHero
          round={r}
          stageLabel={stageLabel}
          locale={locale}
          nextStageKey={nextStageKey}
          onMoveNext={handleNext}
          isMoving={transition.isPending}
          onDelete={handleDelete}
          isDeleting={deleteRound.isPending}
          isMobile={isMobile}
        />

        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, md: 8, lg: 10 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tabs.List>
                <Tabs.Tab value="participants">{`${t('perfReview.tabParticipants')} (${ps.length})`}</Tabs.Tab>
                <Tabs.Tab value="assignments">{t('perfReview.tabAssignments')}</Tabs.Tab>
                <Tabs.Tab value="summarizers">
                  {t('perfReview.tabSummarizers')}
                  {unassignedSummarizers > 0 && (
                    <Badge color="red" variant="light" size="xs" ml="xs">
                      {unassignedSummarizers}
                    </Badge>
                  )}
                </Tabs.Tab>
                {canSeeProgress && (
                  <Tabs.Tab value="progress">{t('perfReview.tabProgress')}</Tabs.Tab>
                )}
                {canWriteSummaries && (
                  <Tabs.Tab value="summary">{t('perfReview.tabSummary')}</Tabs.Tab>
                )}
              </Tabs.List>

              <Tabs.Panel value="participants" pt="md">
                {r.stage === 'draft' && (
                  <Paper withBorder p="md" mb="md">
                    <Group justify="space-between" mb={isAddOpen ? 'md' : 0}>
                      <Text fw={500}>{t('perfReview.addParticipants')}</Text>
                      <ActionIcon variant="subtle" onClick={addControls.toggle}>
                        {isAddOpen ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                      </ActionIcon>
                    </Group>
                    <Collapse in={isAddOpen}>
                      <Stack gap="xs">
                        <Group gap="xs" wrap="wrap">
                          <Text size="sm" c="dimmed">
                            {t('perfReview.quickAdd')}
                          </Text>
                          <Button
                            size="xs"
                            variant="light"
                            disabled={!availableOptions.length}
                            onClick={() => mergeIds(allUsers.map((u) => u._id))}
                          >
                            {t('perfReview.allUsersCount', { count: availableOptions.length })}
                          </Button>
                          {teamsTyped.map((team) => {
                            const ids = [
                              ...(team.members || [])
                                .map((m) => m.user?._id)
                                .filter((uid): uid is string => !!uid),
                              ...(team.lead ? [team.lead._id] : []),
                            ];
                            const newCount = ids.filter((uid) => !isInRound(uid)).length;
                            if (!newCount) return null;
                            return (
                              <Button
                                key={team._id}
                                size="xs"
                                variant="light"
                                onClick={() => mergeIds(ids)}
                              >
                                {t('perfReview.teamCount', { name: team.name, count: newCount })}
                              </Button>
                            );
                          })}
                        </Group>
                        <Group align="flex-end" wrap="nowrap">
                          <MultiSelect
                            placeholder={t('perfReview.pickUsers')}
                            data={availableOptions}
                            value={pickedUsers}
                            onChange={setPickedUsers}
                            searchable
                            clearable
                            style={{ flex: 1 }}
                          />
                          <Button
                            disabled={!pickedUsers.length}
                            loading={addParticipant.isPending}
                            onClick={async () => {
                              const failed: string[] = [];
                              for (const userId of pickedUsers) {
                                try {
                                  await addParticipant.mutateAsync({
                                    roundId: id!,
                                    data: { user: userId },
                                  });
                                } catch {
                                  failed.push(userNameById[userId] || userId);
                                }
                              }
                              setPickedUsers([]);
                              await refetchParticipants();
                              if (failed.length) {
                                showToast({
                                  title: t('perfReview.partiallyFailed'),
                                  message: failed.join(', '),
                                  color: 'red',
                                });
                              } else {
                                showToast({
                                  title: t('perfReview.addedTitle'),
                                  message: t('perfReview.addedMessage', {
                                    count: pickedUsers.length,
                                  }),
                                  color: 'teal',
                                });
                              }
                            }}
                          >
                            {t('perfReview.addWithCount', { count: pickedUsers.length })}
                          </Button>
                        </Group>
                      </Stack>
                    </Collapse>
                  </Paper>
                )}

                <Paper withBorder>
                  <Table verticalSpacing="sm" highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{t('perfReview.colParticipant')}</Table.Th>
                        <Table.Th style={{ width: 80 }}>{t('perfReview.colSelf')}</Table.Th>
                        <Table.Th style={{ width: 110 }}>{t('perfReview.colAssigned')}</Table.Th>
                        <Table.Th>{t('perfReview.colWished')}</Table.Th>
                        {r.stage === 'draft' && <Table.Th style={{ width: 110 }}></Table.Th>}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {ps.map((p) => {
                        const userId = typeof p.user === 'object' ? p.user?._id : p.user;
                        const name = typeof p.user === 'object' ? p.user?.name : p.user;
                        const selfDone = userId
                          ? selfStatusByUser.get(userId) === 'submitted'
                          : false;
                        const assigned = userId ? assignedCountBySubject[userId] || 0 : 0;
                        const wished = p.requestedReviewers || [];
                        return (
                          <Table.Tr key={p._id}>
                            <Table.Td>{name}</Table.Td>
                            <Table.Td>
                              <Badge color={selfDone ? 'teal' : 'gray'} variant="light">
                                {selfDone ? '✓' : '—'}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Badge variant="light" color={assigned ? 'blue' : 'gray'}>
                                {assigned}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              {wished.length ? (
                                <Group gap={4} wrap="wrap">
                                  {wished.slice(0, 4).map((rid) => (
                                    <Badge key={rid} variant="dot" color="gray">
                                      {userNameById[rid] || rid.slice(0, 6)}
                                    </Badge>
                                  ))}
                                  {wished.length > 4 && (
                                    <Tooltip
                                      label={wished
                                        .slice(4)
                                        .map((rid) => userNameById[rid] || rid)
                                        .join(', ')}
                                    >
                                      <Badge variant="light" color="gray">
                                        +{wished.length - 4}
                                      </Badge>
                                    </Tooltip>
                                  )}
                                </Group>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  —
                                </Text>
                              )}
                            </Table.Td>
                            {r.stage === 'draft' && (
                              <Table.Td>
                                <Button
                                  size="xs"
                                  color="red"
                                  variant="subtle"
                                  loading={removeParticipant.isPending}
                                  onClick={async () => {
                                    if (!userId) return;
                                    try {
                                      await removeParticipant.mutateAsync({ roundId: id!, userId });
                                      await refetchParticipants();
                                    } catch (e) {
                                      showToast({
                                        title: t('perfReview.removeFailed'),
                                        message: perfReviewErrorMessage(t, e),
                                        color: 'red',
                                      });
                                    }
                                  }}
                                >
                                  {t('perfReview.remove')}
                                </Button>
                              </Table.Td>
                            )}
                          </Table.Tr>
                        );
                      })}
                      {!ps.length && (
                        <Table.Tr>
                          <Table.Td colSpan={r.stage === 'draft' ? 5 : 4}>
                            <Center py="lg">
                              <Text c="dimmed">{t('perfReview.noParticipants')}</Text>
                            </Center>
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Table.Tbody>
                  </Table>
                </Paper>
              </Tabs.Panel>

              <Tabs.Panel value="assignments" pt="md">
                <PerformanceReviewAssignmentsEditor roundId={id!} />
              </Tabs.Panel>

              <Tabs.Panel value="summarizers" pt="md">
                <SummarizersPanel roundId={id || ''} previousRounds={previousRounds} />
              </Tabs.Panel>

              <Tabs.Panel value="progress" pt="md">
                {prog && (
                  <Group mb="lg" grow>
                    <Paper withBorder p="md">
                      <Group>
                        <RingProgress
                          size={80}
                          thickness={8}
                          sections={[
                            {
                              value: prog.selfForms?.total
                                ? ((prog.selfForms.submitted || 0) / prog.selfForms.total) * 100
                                : 0,
                              color: 'teal',
                            },
                          ]}
                          label={
                            <Center>
                              <Text size="xs" fw={700}>
                                {prog.selfForms?.submitted || 0}/{prog.selfForms?.total || 0}
                              </Text>
                            </Center>
                          }
                        />
                        <div>
                          <Text fw={500}>{t('perfReview.selfEssays')}</Text>
                          <Text size="xs" c="dimmed">
                            {t('perfReview.submittedFromTotal')}
                          </Text>
                        </div>
                      </Group>
                    </Paper>
                    <Paper withBorder p="md">
                      <Group>
                        <RingProgress
                          size={80}
                          thickness={8}
                          sections={[
                            {
                              value: prog.peerForms?.total
                                ? ((prog.peerForms.submitted || 0) / prog.peerForms.total) * 100
                                : 0,
                              color: 'indigo',
                            },
                          ]}
                          label={
                            <Center>
                              <Text size="xs" fw={700}>
                                {prog.peerForms?.submitted || 0}/{prog.peerForms?.total || 0}
                              </Text>
                            </Center>
                          }
                        />
                        <div>
                          <Text fw={500}>{t('perfReview.peerFeedback')}</Text>
                          <Text size="xs" c="dimmed">
                            {t('perfReview.submittedFromAssigned')}
                          </Text>
                        </div>
                      </Group>
                    </Paper>
                    <Paper withBorder p="md">
                      <Group>
                        <RingProgress
                          size={80}
                          thickness={8}
                          sections={[
                            {
                              value: prog.summaries?.total
                                ? ((prog.summaries.published || 0) / prog.summaries.total) * 100
                                : 0,
                              color: 'grape',
                            },
                          ]}
                          label={
                            <Center>
                              <Text size="xs" fw={700}>
                                {prog.summaries?.published || 0}/{prog.summaries?.total || 0}
                              </Text>
                            </Center>
                          }
                        />
                        <div>
                          <Text fw={500}>{t('perfReview.summaries')}</Text>
                          <Text size="xs" c="dimmed">
                            {t('perfReview.publishedPlural')}
                          </Text>
                        </div>
                      </Group>
                    </Paper>
                  </Group>
                )}

                <Group justify="space-between" mb="sm">
                  <Text size="sm" c="dimmed">
                    {t('perfReview.remind.sent', { count: prog?.reminders ?? 0 })}
                  </Text>
                  {owingByUser.size > 0 && (
                    <Button
                      variant="light"
                      color="teal"
                      leftSection={<IconBell size={16} />}
                      loading={remindingKey === 'all'}
                      onClick={confirmRemindAll}
                    >
                      {t('perfReview.remind.all', { count: owingByUser.size })}
                    </Button>
                  )}
                </Group>
                <Paper withBorder>
                  <Table verticalSpacing="sm" highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{t('perfReview.colParticipant')}</Table.Th>
                        <Table.Th style={{ width: 110 }}>{t('perfReview.colSelf')}</Table.Th>
                        <Table.Th style={{ width: 130 }}>{t('perfReview.colPeerGiven')}</Table.Th>
                        <Table.Th style={{ width: 130 }}>
                          {t('perfReview.colPeerReceived')}
                        </Table.Th>
                        <Table.Th style={{ width: 160 }}>{t('perfReview.summaries')}</Table.Th>
                        {isCollecting && (
                          <Table.Th style={{ width: 180 }}>
                            {t('perfReview.remind.column')}
                          </Table.Th>
                        )}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {progressRows.map(({ userId, name, participant: p }) => {
                        const selfStatus = selfStatusByUser.get(userId);
                        const selfDone = selfStatus === 'submitted';
                        const isSelfDraft = selfStatus === 'draft';
                        const remindedAt = owingByUser.get(userId);
                        const hasSelfForm = selfStatusByUser.has(userId);
                        const peerRecvTotal = peerStatsBySubject.total[userId] || 0;
                        const peerRecvSub = peerStatsBySubject.submitted[userId] || 0;
                        const peerGivenTotal = peerStatsByRespondent.total[userId] || 0;
                        const peerGivenSub = peerStatsByRespondent.submitted[userId] || 0;
                        const hasDraftBlocks = (p?.summaryBlocks ?? []).some(
                          (b: { block: string; comment: string }) => b.comment?.trim(),
                        );
                        const summaryState = p?.summaryPublishedAt
                          ? 'published'
                          : p?.summarySharedAt
                            ? 'shared'
                            : p?.summaryDraft || hasDraftBlocks
                              ? 'draft'
                              : 'empty';
                        const summaryBadge = {
                          published: { color: 'teal', label: t('perfReview.statusPublished') },
                          shared: { color: 'blue', label: t('perfReview.statusShared') },
                          draft: { color: 'yellow', label: t('perfReview.statusDraft') },
                          empty: { color: 'gray', label: t('perfReview.statusEmpty') },
                        }[summaryState];
                        return (
                          <Table.Tr key={userId}>
                            <Table.Td>
                              <Group gap="xs" wrap="nowrap">
                                <Text size="sm">{name}</Text>
                                {!p && (
                                  <Badge size="xs" variant="outline" color="gray">
                                    {t('perfReview.reviewerOnly')}
                                  </Badge>
                                )}
                              </Group>
                            </Table.Td>
                            <Table.Td>
                              {hasSelfForm ? (
                                <Badge
                                  color={selfDone ? 'teal' : isSelfDraft ? 'yellow' : 'gray'}
                                  variant="light"
                                >
                                  {selfDone
                                    ? t('perfReview.statusSubmitted')
                                    : isSelfDraft
                                      ? t('perfReview.statusDraft')
                                      : '—'}
                                </Badge>
                              ) : (
                                <Text size="xs" c="dimmed">
                                  {t('perfReview.selfNotApplicable')}
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Badge
                                color={
                                  !peerGivenTotal
                                    ? 'gray'
                                    : peerGivenSub === peerGivenTotal
                                      ? 'teal'
                                      : peerGivenSub
                                        ? 'yellow'
                                        : 'red'
                                }
                                variant="light"
                              >
                                {peerGivenSub}/{peerGivenTotal}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Badge
                                color={
                                  !peerRecvTotal
                                    ? 'gray'
                                    : peerRecvSub === peerRecvTotal
                                      ? 'teal'
                                      : peerRecvSub
                                        ? 'yellow'
                                        : 'red'
                                }
                                variant="light"
                              >
                                {peerRecvSub}/{peerRecvTotal}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              {p ? (
                                <Badge color={summaryBadge.color} variant="light">
                                  {summaryBadge.label}
                                </Badge>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  —
                                </Text>
                              )}
                            </Table.Td>
                            {isCollecting && (
                              <Table.Td>
                                {remindedAt !== undefined && (
                                  <Group gap={6} wrap="nowrap">
                                    <Tooltip label={t('perfReview.remind.one')}>
                                      <ActionIcon
                                        variant="subtle"
                                        color="teal"
                                        aria-label={t('perfReview.remind.oneFor', { name })}
                                        loading={remindingKey === userId}
                                        onClick={() => sendReminders(userId)}
                                      >
                                        <IconBell size={16} />
                                      </ActionIcon>
                                    </Tooltip>
                                    {remindedAt > 0 && (
                                      <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                                        {t('perfReview.remind.at', {
                                          date: new Date(remindedAt).toLocaleDateString(locale, {
                                            day: 'numeric',
                                            month: 'short',
                                          }),
                                        })}
                                      </Text>
                                    )}
                                  </Group>
                                )}
                              </Table.Td>
                            )}
                          </Table.Tr>
                        );
                      })}
                      {!progressRows.length && (
                        <Table.Tr>
                          <Table.Td colSpan={isCollecting ? 6 : 5}>
                            <Center py="lg">
                              <Text c="dimmed">{t('perfReview.noParticipants')}</Text>
                            </Center>
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Table.Tbody>
                  </Table>
                </Paper>
              </Tabs.Panel>

              <Tabs.Panel value="summary" pt="md">
                <Paper withBorder>
                  <Table verticalSpacing="sm" highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: '22%' }}>
                          {t('perfReview.colParticipant')}
                        </Table.Th>
                        <Table.Th style={{ width: 140 }}>{t('perfReview.colStatus')}</Table.Th>
                        <Table.Th>{t('perfReview.preview')}</Table.Th>
                        <Table.Th style={{ width: 130 }}></Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {visibleParticipants.map((p) => {
                        // Prefer composing from structured blocks so the unnamed-block
                        // sentinel ("__unnamed__") doesn't leak through as a heading.
                        // Fall back to the backend-composed string for legacy rounds
                        // that have no `summaryBlocks`.
                        const blocksText = (p.summaryBlocks || [])
                          .filter((b) => b.comment?.trim())
                          .map((b) => {
                            const title =
                              b.block === '__unnamed__' ? t('perfReview.unnamedSection') : b.block;
                            return `## ${title}\n${b.comment}`;
                          })
                          .join('\n\n');
                        const text =
                          blocksText ||
                          (p.summaryPublishedAt ? p.summaryPublished : p.summaryDraft) ||
                          '';
                        const preview = text
                          ? text.length > 90
                            ? text.slice(0, 90) + '…'
                            : text
                          : '—';
                        const status = p.summaryPublishedAt
                          ? 'published'
                          : p.summaryDraft || (p.summaryBlocks || []).some((b) => b.comment?.trim())
                            ? 'draft'
                            : 'empty';
                        return (
                          <Table.Tr key={p._id}>
                            <Table.Td>
                              {typeof p.user === 'object' ? p.user?.name : p.user}
                            </Table.Td>
                            <Table.Td>
                              <Badge
                                color={
                                  status === 'published'
                                    ? 'teal'
                                    : status === 'draft'
                                      ? 'yellow'
                                      : 'gray'
                                }
                                variant="light"
                              >
                                {status === 'published'
                                  ? t('perfReview.statusPublished')
                                  : status === 'draft'
                                    ? t('perfReview.statusDraft')
                                    : t('perfReview.statusEmpty')}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              {text ? (
                                <Tooltip label={text} multiline w={400} withinPortal>
                                  <Text size="sm" c="dimmed" lineClamp={2}>
                                    {preview}
                                  </Text>
                                </Tooltip>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  —
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Button size="xs" variant="light" onClick={() => setSummaryUser(p)}>
                                {t('perfReview.open')}
                              </Button>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                      {!visibleParticipants.length && (
                        <Table.Tr>
                          <Table.Td colSpan={4}>
                            <Center py="lg">
                              <Text c="dimmed">{t('perfReview.noParticipants')}</Text>
                            </Center>
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Table.Tbody>
                  </Table>
                </Paper>
              </Tabs.Panel>
            </Tabs>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4, lg: 2 }}>
            <Stack gap="md" style={isMobile ? undefined : { position: 'sticky', top: 80 }}>
              <StageHistoryCard
                history={stageHistory}
                stageLabel={stageLabel}
                locale={locale}
                userNameById={userNameById}
              />
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>

      {summaryUser && (
        <PerformanceReviewSummaryEditModal
          opened={!!summaryUser}
          onClose={() => setSummaryUser(null)}
          roundId={id!}
          userId={
            typeof summaryUser.user === 'object'
              ? (summaryUser.user?._id ?? '')
              : (summaryUser.user ?? '')
          }
          initialBlocks={summaryUser.summaryBlocks}
          isPublished={!!summaryUser.summaryPublishedAt}
          isShared={!!summaryUser.summarySharedAt}
          initialOutcome={summaryUser}
        />
      )}
    </PageContainer>
  );
};
